import { lazy, Suspense, useEffect, useState, type FormEvent } from "react";
import { ArrowRight, FlaskConical, Link2 } from "lucide-react";
import { api, type InvitationPreview } from "./api.ts";
import { useRun } from "./useRun.ts";
import { ParticipantExperience } from "./participant/ParticipantExperience.tsx";
import { LocalMediaProvider } from "./media/LocalAudioPlayer.tsx";
import type { Identity } from "../shared/types.ts";
const PresenterApp = lazy(() =>
  import("./presenter/PresenterApp.tsx").then((m) => ({
    default: m.PresenterApp,
  })),
);

export function App() {
  const [path, setPath] = useState(location.pathname),
    [identity, setIdentity] = useState<Identity | null>(null),
    [loading, setLoading] = useState(true),
    [localAccess, setLocalAccess] = useState(false),
    [error, setError] = useState("");
  useEffect(() => {
    api
      .identity()
      .then((r) => {
        setIdentity(r.identity);
        setLocalAccess(r.localAccess);
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
    const onPop = () => setPath(location.pathname);
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);
  if (path.startsWith("/join/"))
    return (
      <JoinInvitation
        token={path.slice("/join/".length)}
        onJoined={(id) => {
          const next = "/participant/" + id;
          history.replaceState({}, "", next);
          setPath(next);
        }}
      />
    );
  if (path.startsWith("/participant/"))
    return <ParticipantPage runId={path.slice("/participant/".length)} />;
  if (loading)
    return (
      <div className="auth-page">
        <p role="status">Opening the lab…</p>
      </div>
    );
  if (identity)
    return (
      <Suspense
        fallback={
          <div className="auth-page">
            <p role="status">Opening the workspace…</p>
          </div>
        }
      >
        <PresenterApp
          identity={identity}
          onLogout={async () => {
            await api.logout();
            setIdentity(null);
          }}
        />
      </Suspense>
    );
  return (
    <SignIn localAccess={localAccess} error={error} onLogin={setIdentity} />
  );
}
function SignIn({
  localAccess,
  error: initialError,
  onLogin,
}: {
  localAccess: boolean;
  error: string;
  onLogin: (identity: Identity) => void;
}) {
  const [password, setPassword] = useState(""),
    [busy, setBusy] = useState(false),
    [error, setError] = useState(initialError);
  const signIn = async (local = false) => {
    setBusy(true);
    setError("");
    try {
      onLogin(await (local ? api.localLogin() : api.login(password)));
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <main className="auth-page">
      <section className="auth-panel">
        <div className="auth-mark">
          <FlaskConical size={21} />
          <span>
            sensavera <b>Lab</b>
          </span>
        </div>
        <h1>Sign in to present</h1>
        <p className="auth-intro">
          Choose a story and explore the connected sensavera experience.
        </p>
        <form
          onSubmit={(e: FormEvent) => {
            e.preventDefault();
            void signIn();
          }}
        >
          <label htmlFor="presenter-password">Presenter password</label>
          <input
            id="presenter-password"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
          <button
            className="app-primary"
            disabled={busy || !password}
            type="submit"
          >
            {busy ? "Signing in…" : "Sign in"}
            <ArrowRight size={17} />
          </button>
        </form>
        {localAccess && (
          <button
            className="app-secondary local-access"
            onClick={() => void signIn(true)}
            disabled={busy}
          >
            Open as local presenter
          </button>
        )}
        {error && (
          <p className="app-error" role="alert">
            {error}
          </p>
        )}
        <p className="auth-note">
          Participant phones join through an invitation from their presenter.
        </p>
      </section>
    </main>
  );
}
function JoinInvitation({
  token,
  onJoined,
}: {
  token: string;
  onJoined: (runId: string) => void;
}) {
  const [invitation, setInvitation] = useState<InvitationPreview | null>(null),
    [error, setError] = useState(""),
    [busy, setBusy] = useState(false);
  useEffect(() => {
    api
      .invitation(token)
      .then(setInvitation)
      .catch((e) => setError(e.message));
  }, [token]);
  return (
    <main className="auth-page">
      <section className="auth-panel join-panel">
        <div className="auth-mark">
          <Link2 size={20} />
          <span>sensavera</span>
        </div>
        <p className="auth-eyebrow">PARTICIPANT INVITATION</p>
        <h1>
          {invitation
            ? "Join " + invitation.label + "’s session"
            : "Opening invitation…"}
        </h1>
        {invitation && (
          <>
            <p className="auth-intro">{invitation.runTitle}</p>
            <p>
              Your presenter will coordinate the experience. This invitation
              opens your assigned session.
            </p>
            <button
              className="app-primary"
              disabled={busy}
              onClick={async () => {
                setBusy(true);
                setError("");
                try {
                  await api.join(token);
                  onJoined(invitation.runId);
                } catch (e) {
                  setError((e as Error).message);
                } finally {
                  setBusy(false);
                }
              }}
            >
              {busy ? "Joining…" : "Join session"}
              <ArrowRight size={17} />
            </button>
          </>
        )}
        {error && (
          <p className="app-error" role="alert">
            {error}
          </p>
        )}
      </section>
    </main>
  );
}
function ParticipantPage({ runId }: { runId: string }) {
  // Command failures appear beside the control that sent them; this page reports the connection.
  const { view, problem, connection, busy, dispatch, reconnect } = useRun(
    runId,
    true,
  );
  if (!view || view.role !== "participant" || connection === "ended")
    return (
      <main className="auth-page">
        <section className="auth-panel">
          <div className="auth-mark">sensavera</div>
          <h1>
            {connection === "ended"
              ? "Session access ended"
              : "Participant session"}
          </h1>
          <p role={problem || connection === "ended" ? "alert" : "status"}>
            {problem ||
              (connection === "ended"
                ? "This invitation no longer opens the session. Ask the presenter for a new invitation."
                : "Connecting to your session…")}
          </p>
          {(problem || connection === "ended") && (
            <button className="app-secondary" onClick={reconnect}>
              Try connecting again
            </button>
          )}
        </section>
      </main>
    );
  // Only another controller makes this a mirror; a lost connection pauses input and keeps typed text.
  const mirror =
    view.session.controller.kind !== "device" ||
    view.session.controller.deviceId !== view.clientId;
  const paused = connection !== "live";
  return (
    <LocalMediaProvider
      scope={`${runId}:${view.session.id}:${view.step.id}:${view.session.phase}:${view.session.controller.revision}`}
      unavailable={paused}
    >
      <main className="participant-page">
        <div className="participant-lab-status">
          <span className={"connection-dot " + connection} />
          <span role="status">
            {paused
              ? "Reconnecting · answers are paused"
              : mirror
                ? "Presenter is controlling this session"
                : "Connected to your session"}
          </span>
          {paused ? (
            <button
              type="button"
              className="participant-retry"
              onClick={reconnect}
            >
              Retry now
            </button>
          ) : (
            <span>Authored experience</span>
          )}
        </div>
        {problem && (
          <div className="participant-network-error" role="alert">
            {problem}
          </div>
        )}
        <ParticipantExperience
          view={view}
          onAction={dispatch}
          readOnly={mirror}
          paused={paused}
          busy={busy}
        />
      </main>
    </LocalMediaProvider>
  );
}
